import classNames from '@utils/classnames';
import React from 'react';
import { cva } from 'class-variance-authority';

import { Spinner } from '../Spinner';

/**
 * Button hierarchy — one orange primary action per screen, everything else is
 * secondary or ghost. Destructive actions are red *outlined*, never a solid
 * black or red block.
 *
 * Canonical colours: `primary` | `secondary` | `ghost` | `danger`.
 * The older names (`default`, `default-outline`, `primary-outline`, `subtle`,
 * `danger-outline`) are accepted as aliases and map onto the canonical set.
 */
export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  // aliases
  | 'default'
  | 'default-outline'
  | 'primary-outline'
  | 'danger-outline'
  | 'subtle';

export type ButtonSize = 6 | 8 | 10;

type CanonicalColor = 'primary' | 'secondary' | 'ghost' | 'danger';

const COLOR_ALIASES: Record<ButtonColor, CanonicalColor> = {
  primary: 'primary',
  secondary: 'secondary',
  ghost: 'ghost',
  danger: 'danger',
  default: 'secondary',
  'default-outline': 'secondary',
  'primary-outline': 'secondary',
  'danger-outline': 'danger',
  subtle: 'ghost',
};

export function resolveButtonColor(color: ButtonColor | undefined): CanonicalColor {
  return COLOR_ALIASES[color ?? 'secondary'] ?? 'secondary';
}

export const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap select-none',
    'rounded-md border font-medium outline-hidden transition-colors',
    'disabled:cursor-default',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-brand border-brand text-fg-inverse hover:bg-brand-hover hover:border-brand-hover',
        secondary: 'bg-surface border-border text-fg-secondary hover:bg-surface-hover hover:text-fg',
        ghost: 'bg-transparent border-transparent text-fg-secondary hover:bg-surface-hover hover:text-fg',
        danger: 'bg-surface border-negative-border text-negative-text hover:bg-negative-subtle',
        disabled: 'bg-neutral-subtle border-neutral-subtle text-fg-muted',
        'disabled-ghost': 'bg-transparent border-transparent text-fg-muted',
      },
      size: {
        10: 'h-9 px-3.5 text-sm',
        8: 'h-7 px-2.5 text-xs',
        6: 'h-6 px-2 text-xs',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 10,
    },
  },
);

export function buttonClassName(options: {
  color?: ButtonColor;
  size?: ButtonSize;
  isDisabled?: boolean;
  className?: string;
}) {
  const canonical = resolveButtonColor(options.color);
  const variant = options.isDisabled ? (canonical === 'ghost' ? 'disabled-ghost' : 'disabled') : canonical;
  return classNames(buttonVariants({ variant, size: options.size ?? 10 }), options.className);
}

export interface BaseButtonProps extends React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> {
  color?: ButtonColor;
  size?: ButtonSize;
  isLoading?: boolean;
  iconLeft?: React.ReactElement;
  iconRight?: React.ReactElement;
}
export type Ref = HTMLButtonElement;

export const BaseButton = React.forwardRef<Ref, BaseButtonProps>((props, ref) => {
  const {
    isLoading,
    disabled,
    color = 'secondary',
    size = 10,
    children,
    className,
    iconLeft,
    iconRight,
    type = 'button',
    ...otherProps
  } = props;

  const canonical = resolveButtonColor(color);

  return (
    <button
      {...otherProps}
      type={type}
      disabled={disabled}
      className={buttonClassName({ color, size, isDisabled: disabled, className })}
      ref={ref}
    >
      <span className={classNames('inline-flex items-center justify-center gap-1.5', { invisible: isLoading })}>
        {iconLeft != null && <span className="inline-flex shrink-0 items-center">{iconLeft}</span>}
        {children != null && children !== '' && <span>{children}</span>}
        {iconRight != null && <span className="inline-flex shrink-0 items-center">{iconRight}</span>}
      </span>
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner size={4} color={canonical === 'primary' && !disabled ? 'inverse' : 'default'} />
        </span>
      )}
    </button>
  );
});
